import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const ChangelogModal = ({ open, onClose, version }) => {
  // Changelog data - in a real app, this could be fetched from an API or imported from CHANGELOG.md
  const changelog = [
    {
      version: '1.0.9',
      date: '03-03-2026',
      changes: {
        added: [
          'Initial carry over days for vacation tracking',
          'Email notifications for vacation requests',
          'Bank accounts for vendors',
          'Approval flow for bank accounts and flow for deactivating bank accounts',
          'Monthly revenue/costs and profit trends charts data for previous years',
          'Backend validation for invoice due date',
          'Rate limiting for public routes'
        ],
        changed: [
          "Invoice date is read-only, fixed to today's date",
          'Changed stats on vendor invoices page',
          'Changed default invoices per page to 50'
        ],
        fixed: [
          'Invoice and timesheet files that contain special characters',
          'Total open amount calculation',
          'Initial carryover days calculation',
          'Fixed package vulnerabilities'
        ]
      }
    },
    {
      version: '1.0.8',
      date: '15-12-2025',
      changes: {
        added: [
          'Client invoicing: added company details template, bank account, payment terms templates, excel and pdf download for invoice, send invoice to client',
          'Merge client without account with existing user account',
          'Client can see and comment on timesheets attached to their projects',
          'Country dropdown for vendors and clients',
          'Maximum number of hours for vendors',
          'Maximum invoice amount for OPEX vendors',
          'Show notice on client form for email field',
          'Email notification when comment is added for an invoice',
          'Sanitize financial data from client APIs',
          'Added custom invoice number for vendors',
          'Admin can edit billing units for vendor invoices'
        ],
        changed: [
          'OPEX vendors can view/add comments to invoice',
          'Billing units or timesheet should be added when invoice is created',
          'Downloaded invoice / timesheet file includes custom invoice number in the filename',
          'Default payment terms is 30 days'
        ],
        fixed: [
          'Email templates fixes for Outlook',
          'Invoice status color and label updates',
          'Invoice file should be mandatory',
          'KPIs not calculating pending revenue and total revenue',
          'Opex vendor cannot update / submit rejected invoices',
          'Rates validation - should accept 3 decimal numbers',
          'KPIs revenue: normalized billing units',
          'Client invoicing billing selector'
        ]
      }
    },
    {
      version: '1.0.7',
      date: '08-12-2025',
      changes: {
        added: [
          'Vacation tracking for all users',
          'Vacation Management for Admin',
          'Vacation policy - notice period, default allowance, individual allowance, carryover days',
          'Feature Management for Admin users and dynamic display of features',
          'Upcoming days off card in Dashboard',
          'Unit testing for vacation tracking APIs',
          'Login as vendor',
          'Added 3rd party daily currency update',
          'Vendor default currency'
        ],
        changed: ['React packages updates'],
        fixed: [
          'Admin Dashboard - do not display empty tables',
          'Invoice status color and label updates',
          'KPIs not calculating pending revenue and total revenue'
        ]
      }
    },
    {
      version: '1.0.6',
      date: '05-12-2025',
      changes: {
        added: ['2FA', '2FA enforcement by Admin users'],
        changed: ['Dynamic primary and secondary shades'],
        fixed: ['Vendors should not be able to see current timetracker for subcontractor', 'Stop timetracker error for subcontractors']
      }
    },
    {
      version: '1.0.5',
      date: '26-11-2025',
      changes: {
        added: [
          'Login for subcontractors',
          'Admin and Vendor invite to subcontractors',
          'Timetracking for vendors and subcontractors',
          'Payment terms for vendors',
          'Encrypted sensitive financial data',
          'API unit testing'
        ],
        changed: ['Dynamic primary and secondary shades'],
        fixed: ['Vendors cannot add invoice due date before current date + payment terms']
      }
    },
    {
      version: '1.0.4',
      date: '17-11-2025',
      changes: {
        added: [
          'Changelog modal for admins to view version history',
          'Soft delete for users',
          'TableActions component now supports restore functionality',
          'DataTable and TableFilters integration for deleted users tab',
          'Vendor invoices pagination',
          'Add send invitation shortcut on User tabs'
        ],
        changed: [
          'UI components refactoring: tables, pagination, actions, statistics cards, status colors and lables, confirm dialog, snackbar'
        ],
        fixed: [
          'Do not show OPEX vendors in project assignment modal',
          'Invoice details - hide reject button when invoice is already approved'
        ]
      }
    },
    {
      version: '1.0.3',
      date: '12-11-2025',
      changes: {
        changed: [
          'Email invitations queue',
          'Allow admins to edit vendor company details',
          'Users view refactoring',
          'Bug fixing and improvements'
        ]
      }
    },
    {
      version: '1.0.2',
      date: '11-11-2025',
      changes: {
        added: [
          'Malware detection for file uploads',
          'Grouped vendor invoices by billing month',
          'Added default currency for admin, currency conversion, project default currency',
          'OPEX Vendor role',
          'Subcontractors for vendors, timesheets per subcontractors',
          'Bug fixing and improvements'
        ],
        changed: ['Improved admin vendor invoices list with server-side pagination']
      }
    },
    {
      version: '1.0.1',
      date: '03-11-2025',
      changes: {
        fixed: ['Bug fixing and improvements']
      }
    },
    {
      version: '1.0.0',
      date: '31-10-2025',
      changes: {
        added: [
          'Initial release',
          'User authentication and authorization',
          'Vendor management',
          'Client management',
          'Invoice management (AP/AR)',
          'Timesheet tracking',
          'Project management',
          'Dashboard with statistics',
          'Admin panel',
          'Email notifications',
          'Multi-role support (Admin, Vendor, Client)'
        ]
      }
    }
  ];

  const getChangeTypeColor = (type) => {
    switch (type) {
      case 'added':
        return 'success';
      case 'changed':
        return 'primary';
      case 'fixed':
        return 'warning';
      case 'removed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getChangeTypeLabel = (type) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: { maxHeight: '80vh' }
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5" component="div">
            Changelog
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary'
            }}
          >
            Current version: {version}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {changelog.map((release, index) => (
          <Box key={release.version} sx={{ mb: index < changelog.length - 1 ? 4 : 0 }}>
            {/* Version Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6" component="div">
                v{release.version}
              </Typography>
              <Chip label={release.date} size="small" variant="outlined" />
              {release.version === version && <Chip label="Current" size="small" color="primary" />}
            </Box>

            {/* Changes by Type */}
            {Object.entries(release.changes).map(([type, items]) => (
              <Box key={type} sx={{ mb: 2 }}>
                <Chip label={getChangeTypeLabel(type)} color={getChangeTypeColor(type)} size="small" sx={{ mb: 1 }} />
                <List dense disablePadding>
                  {items.map((item, itemIndex) => (
                    <ListItem key={itemIndex} sx={{ py: 0.5, pl: 2 }}>
                      <ListItemText
                        primary={item}
                        slotProps={{
                          primary: {
                            variant: 'body2'
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}

            {index < changelog.length - 1 && <Divider sx={{ mt: 3 }} />}
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
};

ChangelogModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  version: PropTypes.string.isRequired
};

export default ChangelogModal;
